import { useState, useEffect } from 'react'
import { Collapse, Stack } from '@mantine/core'
import styled from 'styled-components'

import { AccordionMultiSelectProps } from './accordion-multi-select.types'

import { Checkbox } from '../checkbox'
import { Icon } from '../icon'

const StyledIconBtn = styled.button`
  ${({ theme }) =>
    `
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${theme.sizes[5]};
    height: ${theme.sizes[5]};
    padding: 0;
    border: none;
    background-color: transparent;
    cursor: pointer;
  `}
`

const StyledIconPlaceholder = styled.div`
  ${({ theme }) =>
    `
    width: ${theme.sizes[5]};
    height: ${theme.sizes[5]};
  `}
`

const StyledContentStack = styled(Stack)<{ maxHeight?: string }>`
  ${({ theme, maxHeight }) =>
    `
    padding: ${theme.sizes[3]} ${theme.sizes[4]};
    gap: ${theme.sizes[4]};
    overflow-y: auto;
    ${maxHeight ? `max-height: ${maxHeight};` : ''}
  `}
`

const StyledOptionsStack = styled(Stack)`
  ${({ theme }) =>
    `
    margin-left: ${theme.sizes[16]};
    margin-top: ${theme.sizes[4]};
    gap: ${theme.sizes[4]};
  `}
`

export const AccordionMultiSelect = ({
  'data-testid': dataTestId = 'accordion-multi-select',
  data,
  value = [],
  onChange,
  showSelectAll = true,
  selectAllLabel = 'Select All',
  expandedGroups,
  maxHeight,
  flattenSingleItemGroups = true,
}: AccordionMultiSelectProps) => {
  // Internal expansion state (fallback when not controlled)
  const [internalExpanded, setInternalExpanded] = useState<{
    [key: string]: boolean
  }>(() => {
    const initialState: { [key: string]: boolean } = {}
    Object.keys(data).forEach((groupName) => {
      initialState[groupName] = false
    })
    return initialState
  })

  // User's explicit toggle overrides
  const [userOverrides, setUserOverrides] = useState<{
    [key: string]: boolean
  }>({})

  // Reset state when data structure changes (groups added/removed)
  // Only compare the keys, not the entire data object reference
  const dataKeys = Object.keys(data).sort().join(',')

  useEffect(() => {
    setUserOverrides({})
    setInternalExpanded(() => {
      const initialState: { [key: string]: boolean } = {}
      Object.keys(data).forEach((groupName) => {
        initialState[groupName] = false
      })
      return initialState
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKeys])

  // Three-tier priority: User overrides > expandedGroups prop > Internal state
  const isGroupExpanded = (groupName: string): boolean => {
    // 1. Check if user has explicitly overridden this group
    if (groupName in userOverrides) {
      return userOverrides[groupName]
    }

    // 2. Check if expandedGroups prop says it should be expanded
    if (expandedGroups?.includes(groupName)) {
      return true
    }

    // 3. Otherwise use internal default state
    return internalExpanded[groupName] ?? false
  }

  const toggleGroup = (group: string) => {
    const currentState = isGroupExpanded(group)

    // Set explicit override
    setUserOverrides((prev) => ({
      ...prev,
      [group]: !currentState,
    }))

    // Also update internal state for when prop is removed
    setInternalExpanded((prev) => ({
      ...prev,
      [group]: !currentState,
    }))
  }

  const handleChange = (newValue: string[]) => {
    onChange?.(newValue)
  }

  // Calculate all available values
  const allValues = Object.values(data)
    .flat()
    .map((option) => option.value)

  const isAllSelected = allValues.every((val) => value.includes(val))
  const isIndeterminate = value.length > 0 && !isAllSelected

  const handleSelectAll = () => {
    if (isAllSelected) {
      handleChange([])
    } else {
      handleChange(allValues)
    }
  }

  const getGroupCheckboxState = (options: typeof data[keyof typeof data]) => {
    const groupValues = options.map((option) => option.value)
    const selectedInGroup = groupValues.filter((val) => value.includes(val))

    if (selectedInGroup.length === 0) {
      return { checked: false, indeterminate: false }
    }
    if (selectedInGroup.length === groupValues.length) {
      return { checked: true, indeterminate: false }
    }
    return { checked: false, indeterminate: true }
  }

  const handleGroupCheckboxChange = (
    options: typeof data[keyof typeof data]
  ) => {
    const groupValues = options.map((option) => option.value)
    const selectedInGroup = groupValues.filter((val) => value.includes(val))
    const otherValues = value.filter((val) => !groupValues.includes(val))

    if (selectedInGroup.length === groupValues.length) {
      handleChange(otherValues)
    } else {
      handleChange([...otherValues, ...groupValues])
    }
  }

  return (
    <StyledContentStack data-testid={dataTestId} maxHeight={maxHeight}>
      {showSelectAll && (
        <Checkbox
          label={selectAllLabel}
          name={`${dataTestId}-select-all`}
          dataTestId={`${dataTestId}-select-all`}
          variant={isIndeterminate ? 'indeterminate' : 'default'}
          active={isAllSelected || (!isAllSelected && isIndeterminate)}
          onChange={handleSelectAll}
        />
      )}

      {Object.entries(data).map(([groupName, options]) => {
        const groupState = getGroupCheckboxState(options)
        const expanded = isGroupExpanded(groupName)
        const isSingleItemGroup =
          flattenSingleItemGroups && options.length === 1

        return (
          <div
            key={`${groupName}-group`}
            data-testid={`${dataTestId}-group-${groupName}`}
          >
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              {isSingleItemGroup ? (
                <StyledIconPlaceholder style={{ marginRight: '0.25rem' }} />
              ) : (
                <StyledIconBtn
                  onClick={() => toggleGroup(groupName)}
                  data-testid={`${dataTestId}-toggle-${groupName}`}
                  style={{ marginRight: '0.25rem' }}
                >
                  <Icon
                    name={expanded ? 'chevron-down' : 'chevron-right'}
                    color="gray600"
                    size="s"
                  />
                </StyledIconBtn>
              )}
              <Checkbox
                label={groupName}
                name={`${dataTestId}-group-checkbox-${groupName}`}
                dataTestId={`${dataTestId}-group-checkbox-${groupName}`}
                variant={groupState.indeterminate ? 'indeterminate' : 'default'}
                active={
                  groupState.checked ||
                  (!groupState.checked && groupState.indeterminate)
                }
                onChange={() => handleGroupCheckboxChange(options)}
              />
            </div>
            {!isSingleItemGroup && (
              <Collapse in={expanded}>
                <StyledOptionsStack>
                  {options.map((option) => (
                    <Checkbox
                      key={`${groupName}-${option.value}-item`}
                      label={option.label}
                      name={`${dataTestId}-option-checkbox-${option.label}`}
                      dataTestId={`${dataTestId}-option-checkbox-${option.label}`}
                      active={value.includes(option.value)}
                      value={option.value}
                      variant="default"
                      onChange={() => {
                        if (value.includes(option.value)) {
                          handleChange([
                            ...value.filter((v) => v !== option.value),
                          ])
                        } else {
                          handleChange([...value, option.value])
                        }
                      }}
                    />
                  ))}
                </StyledOptionsStack>
              </Collapse>
            )}
          </div>
        )
      })}
    </StyledContentStack>
  )
}
